/**
 * Media utility functions for handling media URLs, formatting, and other common operations
 */

// Get the base API and uploads URLs from environment variables
const apiUrl = process.env.REACT_APP_API_URL || '/api';
const uploadsUrl = process.env.REACT_APP_UPLOADS_URL || '/uploads';

/**
 * Fix file path by removing any "uploads" prefix
 * @param {string} filePath - The file path to fix
 * @returns {string} The fixed media URL
 */
export const getMediaUrl = (filePath) => {
  if (!filePath) return '/placeholder-image.jpg';
  // Remove any leading slash and uploads/ prefix if present
  const fixedFilePath = filePath.replace(/^\/?(uploads\/)?/, '');
  return `${uploadsUrl}/${fixedFilePath}`;
};

/**
 * Get the URL for a profile image (profile, cover, or lessons)
 * @param {string} imagePath - The image path from the profile
 * @param {string} defaultImage - The default image to use if imagePath is not provided
 * @returns {string} The complete image URL
 */
export const getProfileImageUrl = (imagePath, defaultImage = '/placeholder-image.jpg') => {
  if (!imagePath || imagePath.trim() === '') return defaultImage;
  
  // Clean up the path by removing any potential duplicate 'uploads/' prefix
  const fixedPath = imagePath.replace(/^\/?(uploads\/)+/, '');
  return `${uploadsUrl}/${fixedPath}`;
};

/**
 * Get optimized image URL based on context (thumbnail vs. lightbox)
 * @param {string} filePath - The file path
 * @param {string} context - The context (thumbnail or lightbox)
 * @returns {string} The optimized image URL
 */
export const getOptimizedImageUrl = (filePath, context = 'thumbnail') => {
  if (!filePath) return '/placeholder-image.jpg';
  
  // Clean up the path by removing any potential duplicate 'uploads/' prefix
  const fixedPath = filePath.replace(/^\/?(uploads\/)+/, '');
  const baseUrl = `${uploadsUrl}/${fixedPath}`;
  
  // Use a cache key that is stable for the whole session so the SAME image
  // always resolves to the SAME URL across renders. Using a per-call value
  // (e.g. Date.now()) here causes the <img> src to change on every render,
  // which makes the browser continuously re-fetch the image and the loading
  // spinner flash/never settle (and defeats the browser cache on retries).
  let cacheKey = sessionStorage.getItem('gallery_cache_key');
  if (!cacheKey) {
    cacheKey = `s${Date.now().toString(36)}`;
    sessionStorage.setItem('gallery_cache_key', cacheKey);
  }
  
  return `${baseUrl}?v=${cacheKey}`;
};

/**
 * Format time for audio/video player
 * @param {number} seconds - The time in seconds
 * @returns {string} Formatted time string (e.g., "1:23")
 */
export const formatTime = (seconds) => {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

/**
 * Calculate image height for masonry layout
 * @param {number} index - The index of the image
 * @returns {string} The calculated height
 */
export const getImageHeight = (index) => {
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // On mobile, use more consistent heights
  if (isMobile) {
    return window.innerWidth <= 480 ? '250px' : '180px';
  }
  
  // On desktop, create varying heights for a more natural masonry effect
  if (index % 5 === 0) return '400px';
  if (index % 5 === 1) return '350px';
  if (index % 5 === 2) return '300px';
  if (index % 5 === 3) return '450px';
  return '380px';
};

/**
 * Filter out profile and cover images from media items
 * @param {Array} mediaItems - The media items to filter
 * @returns {Array} Filtered media items
 */
export const filterPersonalMedia = (mediaItems) => {
  if (!Array.isArray(mediaItems)) return [];
  return mediaItems.filter(item => 
    !(item.category === 'personal' && 
      (item.title?.toLowerCase().includes('profile') || 
       item.title?.toLowerCase().includes('cover') ||
       item.description?.toLowerCase().includes('profile') ||
       item.description?.toLowerCase().includes('cover')))
  );
};

/**
 * Get optimized video URL for different devices, especially iOS
 * @param {string} filePath - The file path
 * @param {string} fileType - The file type (MIME type)
 * @param {boolean} isIOS - Whether the device is running iOS
 * @returns {Object} Object containing different format URLs for video
 */
export const getOptimizedVideoUrl = (filePath, fileType, isIOS = false) => {
  if (!filePath) return { original: '/placeholder-video.mp4' };
  
  const baseUrl = getMediaUrl(filePath);
  let cacheKey = sessionStorage.getItem('video_cache_key');
  
  if (!cacheKey) {
    cacheKey = `v${Date.now().toString(36)}`;
    sessionStorage.setItem('video_cache_key', cacheKey);
  }
  
  // Create URLs for different formats
  const result = {
    original: `${baseUrl}?v=${cacheKey}`,
    mp4: `${baseUrl}?format=mp4&v=${cacheKey}`,
    webm: `${baseUrl}?format=webm&v=${cacheKey}`
  };
  
  // For iOS devices, prioritize MP4 format
  if (isIOS) {
    result.primary = result.mp4;
  } else {
    // For other devices, use the original format as primary
    result.primary = result.original;
  }
  
  return result;
};

/**
 * Detect if the current device is running iOS
 * @returns {boolean} True if the device is running iOS
 */
export const detectIOSDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod|macintosh/.test(userAgent) && 'ontouchend' in document;
};

/**
 * Generate srcSet for document thumbnails with high DPI variants
 * @param {string} thumbnailPath - The thumbnail path from the document
 * @returns {Object} Object containing src and srcSet for the image
 */
export const getDocumentThumbnailSrcSet = (thumbnailPath) => {
  if (!thumbnailPath) return { src: null, srcSet: null };
  
  const src = getMediaUrl(thumbnailPath);
  let srcSet = null;
  
  if (typeof thumbnailPath === 'string') {
    let base;
    if (thumbnailPath.includes('.1@1x.png')) {
      // PDF2PIC format: document-123-thumb.1@1x.png
      base = src.replace('.1@1x.png', '');
      srcSet = `${src} 1x, ${base}.1@2x.png 2x, ${base}.1@3x.png 3x`;
    } else if (thumbnailPath.endsWith('@1x.png')) {
      // Standard format: document-123-thumb@1x.png
      base = src.replace('@1x.png', '');
      srcSet = `${src} 1x, ${base}@2x.png 2x, ${base}@3x.png 3x`;
    } else if (thumbnailPath.endsWith('.png')) {
      // Path has .png but no @1x suffix, check for both standard and .1 variants
      base = src.replace('.png', '');
      srcSet = `${src} 1x, ${base}@2x.png 2x, ${base}@3x.png 3x, ${base}.1@2x.png 2x, ${base}.1@3x.png 3x`;
    } else {
      // Extensionless path, try both standard and .1 variants
      base = src;
      srcSet = `${src}@1x.png 1x, ${base}@2x.png 2x, ${base}@3x.png 3x, ${base}.1@1x.png 1x, ${base}.1@2x.png 2x, ${base}.1@3x.png 3x`;
    }
  }
  
  return { src, srcSet };
};

export { apiUrl, uploadsUrl }; 